// The load path (P2.5) — FR-01, wired end to end.
//
// file → decode → track memory → analysis worker → session store, with the failures each step can
// actually produce turned into something a human can read.
//
// **This is orchestration living in a hook rather than a module, and that is P2.2's decision being
// honoured rather than a shortcut.** BACKLOG-NOTES, P2.2: "The orchestrator is an effect, not a
// module… The real seam belongs with the control path at P2.8, where keyboard, pointer and (at
// FF-1) MIDI all need translating and the pattern has more than one instance to generalise from."
// Loading is the second instance, not the third; extracting a module now would mean designing the
// generalisation from two examples that do not yet disagree with each other.
//
// Boundary note: this file imports both `/audio` and `/state`, which nothing under `/audio` may do
// (`engines-no-store`, machine-enforced). That is the direction working as intended — `ui` is above
// both and is the layer allowed to introduce them.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AnalysisHost, readFramesDone, type TrackAnalysis } from '../../audio/analysis';
import { bestTempoMatchRate, type SyncGrid } from '../../platform/types/sync';
import { AnalysisCache, hashContent } from '../../library';
import { cuesFor, type CachedHotCues } from '../../platform/types/library';
import {
  HOT_CUE_COUNT,
  loopQuantization,
  type LoopQuantization,
} from '../../platform/types/loop';
import {
  MANUAL_BPM_MAX,
  MANUAL_BPM_MIN,
  bpmOf,
  currentTapRun,
  effectiveConfidence,
  effectiveGrid,
  fitTaps,
  nudgeGrid,
  overrideFrom,
  scaleGridTempo,
  tapStage,
  type GridOverride,
  type Tap,
} from '../../platform/types/grid-edit';
import {
  ANALYSIS_SCHEMA_VERSION,
  CACHE_MISS_REASON,
  type CachedAnalysis,
  type CachedGridOverride,
  type DecodedTrackIdentity,
} from '../../platform/types/library';
import { computeWaveformMipmapLayout } from '../../platform/types/waveform-mipmap';
import {
  normalizedToRate,
  rateToNormalized,
  toEngineCommand,
  type AudioGraph,
  DEFAULT_PITCH_RANGE,
  PITCH_CONTROL,
  PITCH_NUDGE,
  PITCH_RANGES,
  type Deck,
  type DeckLatencyInfo,
  type PitchBend,
  type UnderrunReport,
} from '../../audio/engine';
import type { DistributionMs } from '../../platform/sab/histogram';
import {
  decodeFailureMessage,
  dragCarriesFile,
  verifyFormat,
} from '../../audio/engine/formats';
import {
  ControlBus,
  controlEvent,
  toTransportEvent,
  TRANSPORT_CONTROL,
} from '../../control';
import { bindingsForDeck, KeyboardSource } from '../../control/sources';
import type { ControlId } from '../../platform/types/control';
import type { DeckId } from '../../platform/types/control';
import { DECK_DEFAULT_TRACK_GAIN, DECK_MAX_RATE } from '../../platform/types/deck';
import { isRunning, type TransportPhase } from '../../platform/types/transport';
import { useSessionStore } from '../../state/store';

/**
 * Where a load has got to.
 *
 * `decoding` and `analysing` are separate because they fail differently and take different amounts
 * of time — decode is the browser's and is usually the longer one; analysis is ours and is the one
 * with a measured budget (FR-11). Collapsing them into "loading" would hide which half was slow the
 * first time somebody asks.
 */
export type LoadPhase = 'empty' | 'decoding' | 'analysing' | 'ready' | 'failed';

/**
 * How this hook reaches the one `AudioContext` (P3.7).
 *
 * A function rather than a graph, because the graph cannot exist until a user gesture — an
 * `AudioContext` opened outside one starts suspended and stays there, silently. Both decks call the
 * same memoised opener from `App`, so whichever track is dropped first opens the context and the
 * second deck joins it.
 */
export type OpenAudioGraph = () => Promise<AudioGraph>;

/**
 * The app-level resources a deck borrows (P3.8).
 *
 * An object rather than more positional parameters. P3.7 added the first of these and P3.8 the
 * second, and both are the same shape of thing: **one per app, opened lazily, shared by both decks.**
 * A third would have made the call site a row of anonymous functions.
 *
 * Both are memoised by `App`, so whichever deck is used first opens the resource and the other joins
 * it — one `AudioContext`, one IndexedDB connection.
 */
export interface TrackLoaderDeps {
  readonly openGraph: OpenAudioGraph;
  /**
   * FR-12's cache, or a resolved `null` where the browser will not have one.
   *
   * Nullable all the way through rather than a no-op implementation: "there is no cache" is a state
   * worth being able to see, and a silent no-op is indistinguishable from one that never hits — the
   * failure this repo has shipped at P0.1, P0.10 and P2.12.
   */
  readonly openCache: () => Promise<AnalysisCache | null>;
}

/** Where a load's analysis came from (P3.8). Null before anything has been loaded. */
export type AnalysisSource = 'cache' | 'worker';

/** What one deck needs to know about the other in order to sync to it (P3.9). */
export interface SyncPartner {
  readonly beatGrid: SyncGrid | null;
  /** The partner's current playback rate — sync matches where it *is*, not its track's own tempo. */
  readonly pitchRate: number;
}

export interface TrackLoaderApi {
  /**
   * Which deck this loader drives (P3.7).
   *
   * Exposed so the components below take one prop rather than two and cannot be handed a mismatched
   * pair — a `PitchFader` given deck B's loader and deck A's id would publish onto the right bus
   * under the wrong id, which is a bug with no symptom until someone wonders why a fader is dead.
   */
  readonly deckId: DeckId;
  readonly phase: LoadPhase;
  /** Null unless `phase === 'failed'`. Always something a user can act on. */
  readonly error: string | null;
  /** True while a file is dragged over the deck — UX §5.3's identity-coloured outline. */
  readonly dropActive: boolean;
  /** Drop → mipmaps ready, in milliseconds. FR-11's ≤2 s, measured. */
  readonly elapsedMillis: number | null;
  /** How long the worker itself took, of that total. */
  readonly analysisMillis: number | null;
  readonly trackTitle: string | null;
  readonly durationSeconds: number | null;
  /** The mipmaps. P2.6 draws from this; P2.5 only proved it arrives. */
  readonly analysis: TrackAnalysis | null;
  /**
   * Whether this track's analysis was computed or recalled (P3.8).
   *
   * **Exported because FR-12 is a claim that has to be checkable.** P3.8's verify asks to "assert
   * the pipeline did not run, not merely that it was fast", and a duration cannot tell those apart —
   * a fast machine and a cache hit look identical in milliseconds. This says which happened, and the
   * readout under the deck shows it, so the claim is falsifiable by looking.
   */
  readonly analysisSource: AnalysisSource | null;
  /** Why the cache did not hit, in words, or null when it did (or was never consulted). */
  readonly cacheMissReason: string | null;
  /**
   * The FR-26 signal ring this deck publishes into (P2.9).
   *
   * One buffer for the life of the deck, unlike `statusBuffer` which is rebuilt per track — so a
   * consumer can hold it across loads. The waveform reads its playhead from here.
   *
   * **Shared with the other deck since P3.7** — it belongs to the graph, and each deck writes its
   * own source block into the same frame. A consumer must therefore say *which* source it wants;
   * the waveform still reads `DECK_A` because there is still one waveform lane (P3.13 adds the
   * second, and the source index with it).
   */
  readonly signals: SharedArrayBuffer | null;
  readonly frameCount: number | null;
  readonly sampleRateHz: number | null;

  /**
   * The transport phase, read from the audio thread (P2.7).
   *
   * Not a mirror of what the UI last asked for: `previewing` returns to `paused` when a finger
   * lifts, and `ended` fires with nobody pressing anything, so local state would be wrong within
   * seconds of the first cue press.
   */
  readonly transportPhase: TransportPhase;
  /** True while the playhead is advancing — `playing` or `previewing`. What UX §5.4's ribbon seek
   * rule asks about: a preview is live audio, so the ribbon must refuse a seek during one too. */
  readonly playing: boolean;

  /**
   * The pointer's three gestures — **published onto the control bus, not sent to the deck** (P2.8).
   *
   * They read like direct calls and are not: each one publishes a `ControlEvent` with
   * `source: 'pointer'`, which the same subscriber and the same mapping table handle as the
   * keyboard's. That is what makes P2.8's "indistinguishable from the pointer path" literally true
   * rather than a claim about two paths that happen to agree.
   */
  pressPlay: () => void;
  pressCue: () => void;
  releaseCue: () => void;
  /** Which keys are bound, for FR-40's future cheat sheet and for the hint under the transport. */
  readonly keyBindings: readonly { code: string; label: string }[];

  /**
   * FR-10's normalisation, and **the switch that makes it checkable** (P3.2).
   *
   * The claim FR-10 makes is about what two tracks sound like next to each other, and no unit test
   * can hear that. So the gain has an off position: with it on, two differently-mastered tracks
   * should sit at the same perceived level; with it off, they should obviously not. A user who
   * cannot turn a correction off cannot tell a working correction from a broken one.
   */
  readonly normalise: boolean;
  setNormalise: (on: boolean) => void;
  /** What the deck is applying right now, linear. 1 while bypassed, and 1 before loudness lands. */
  readonly appliedGain: number;

  /**
   * FR-05's pitch fader (P3.6) — normalised 0…1, centre is 0%.
   *
   * **The first control in this app that streams into the audio thread**, and therefore the first
   * thing that feeds Architecture §4.5's latency probe outside `/bench`. Every move publishes a
   * `ControlEvent` on the same bus the keyboard and the transport buttons use, which is what makes
   * a MIDI jog wheel at FF-1 a source rather than a rewrite.
   */
  readonly pitchFader: number;
  setPitchFader: (normalized: number) => void;
  /** One of `PITCH_RANGES`. A resolution choice, not a tempo one — see `control-mapping.ts`. */
  readonly pitchRange: number;
  setPitchRange: (rangeFraction: number) => void;
  /** Which nudge is held. A temporary bend that returns, not a second fader (FR-05). */
  readonly pitchBend: PitchBend;
  /**
   * Press or release a nudge, in a named direction.
   *
   * The direction is passed on release as well as on press, so `−` releasing publishes
   * `deck.a.nudge.down` at 0 rather than "some nudge stopped". Both end in a bend of zero today, and
   * a bus whose record of what the user did is approximate is a bus FR-39's remapping UI and FF-1's
   * MIDI learn would both have to work around.
   */
  nudge: (direction: -1 | 1, held: boolean) => void;
  /** The rate the deck is actually running at, fader and bend combined. 1 is unchanged. */
  readonly pitchRate: number;

  /**
   * This track's beatgrid, or null when no tempo was detected (P3.9).
   *
   * Exposed so the *other* deck can compute a tempo match against it. Derived from the analysis
   * rather than stored, so a re-analysis or FR-13's manual correction cannot leave a stale copy.
   */
  readonly beatGrid: SyncGrid | null;

  /**
   * FR-13's manual grid tools (P3.12).
   *
   * `beatGrid` above is already the *corrected* grid, so Sync, the audio thread and the loop
   * quantizer need to know nothing about any of this. What follows is only what the tools
   * themselves need in order to be operated and to say what they did.
   */
  readonly gridOverride: GridOverride | null;
  /** The detector's own answer, kept so the panel can show what a reset would go back to. */
  readonly detectedGrid: SyncGrid | null;
  /** Taps in the current run, against `TAP_TRUSTWORTHY`. Zero between runs. */
  readonly tapCount: number;
  /**
   * The tempo the current run would produce, in BPM, while it is still too short to apply.
   *
   * Null in every other case — before four taps, and once the run is long enough that the grid has
   * actually been stored. **A number here means nothing downstream has seen it**: no override, no
   * grid on the waveform, no Sync target. It exists so the user can watch the run converge instead
   * of tapping thirty-two times into silence. See `tapStage`.
   */
  readonly tapPreviewBpm: number | null;
  /** What the last grid gesture could not do, or advice about the one it just did. Null when there
   * is nothing to say — the tap count is the ordinary feedback. */
  readonly gridNote: string | null;
  tapGrid: () => void;
  resetTaps: () => void;
  /** Positive is later, matching the anchor it moves. See `GRID_NUDGE_MILLIS`. */
  nudgeGridBy: (millis: number) => void;
  scaleGrid: (factor: 0.5 | 2) => void;
  /** Back to the detected grid. Deletes the stored correction rather than storing an absence. */
  clearGridOverride: () => void;

  /**
   * FR-07 — match this deck's tempo to the partner's and align its beat phase, in one action.
   *
   * **Two halves in a fixed order**, and P3.9's rule requires both the separation and the sequence:
   * tempo is a rate change through P3.6's mapping path and is a *setting* that survives; phase is a
   * one-shot the audio thread performs. Tempo first, because a phase aligned between decks whose
   * effective tempos differ walks straight back out.
   *
   * **Sync is not a mode.** Nothing here is remembered, nothing keeps correcting, and there is
   * nothing to "drop": the deck is left playing at whatever rate it reached, exactly as if the fader
   * had been moved by hand. Nudging while synced is just nudging.
   */
  sync: (partner: SyncPartner) => void;
  /** Why the last sync could not do what was asked, or null. Cleared by the next successful one. */
  readonly syncNote: string | null;

  /**
   * FR-03's eight cue points, in frames, `NaN` for empty (P3.10).
   *
   * Polled from the audio thread's status buffer at 10 Hz rather than mirrored, because `set` means
   * "wherever the playhead is" and only that thread knows. Ten times a second is imperceptible for a
   * pad lighting up and costs eight reads.
   */
  readonly hotCues: readonly number[];
  hotCue: (action: 'set' | 'clear' | 'jump', slot: number) => void;

  /** FR-04's loop, as the audio thread has it. */
  readonly loopActive: boolean;
  /** Whether an auto loop would snap to the grid, and why not when it would not. */
  readonly loopQuantization: LoopQuantization;
  loop: (action: 'auto' | 'in' | 'out' | 'exit' | 'reloop', beats?: number) => void;
  /**
   * The loop's boundaries in seconds, as the audio thread has them, or null when none is running.
   *
   * A function rather than a value, like `readSyncPhaseError`: the boundaries belong to the audio
   * thread and a readout polls them at display rate rather than putting them through React (Stack §2).
   */
  readLoop: () => { readonly startSeconds: number; readonly endSeconds: number } | null;
  /**
   * The audio thread's own measurement of the phase error against the other deck, in seconds.
   *
   * A function, not a value: it changes every block and AC-2 is a claim about the audio thread's
   * number, so putting it through React state would be both wrong and expensive (Stack §2).
   */
  readSyncPhaseError: () => number;

  /** Playhead in seconds, read straight from the audio thread's buffer. A function, not a value:
   * FR-09's readouts poll it from rAF and must not put a 60 Hz number through React (Stack §2). */
  readPositionSeconds: () => number;
  readCueSeconds: () => number;
  /** This deck's own level, peak-across-the-interval (P2.9). Null when nothing was published. */
  readDeckPeak: () => number | null;

  /**
   * A window of command→audible latency, consumed (P2.10, re-pointed at the deck at P2.12).
   *
   * Exposed through the loader rather than reaching for the deck directly, because the deck is this
   * hook's private ref and the alternative is handing callers a mutable engine handle. Null when no
   * deck exists yet or the window is under `minimumCount`.
   */
  takeLatencyWindow: (minimumCount: number) => DistributionMs | null;
  /** What this device costs before a sample is audible. Null until a deck exists. */
  readonly latencyInfo: DeckLatencyInfo | null;
  /**
   * Take one reading of the audio clock against the wall clock (P4.16) — Architecture §12's
   * watchdog, which had a counter and nothing feeding it.
   *
   * ⚠️ **Until P4.16 nothing outside `/bench` ever called this**, so the underrun counter read zero
   * in every real session — not because the audio thread was healthy but because nobody had asked
   * it. That is this repo's oldest failure shape, and it was sitting inside the very instrument
   * built to detect it.
   *
   * Cheap by construction: two clock reads and a subtraction, on the main thread, costing the audio
   * thread nothing. See `clock-drift.ts` on why the instrument is not inside the patient.
   */
  sampleClockDrift: () => void;
  /**
   * What those readings add up to. Null until a deck exists.
   *
   * A getter for the same reason `latencyInfo` is one — the deck does not exist until the first
   * load, and a value captured at mount would be null for the life of the hook.
   */
  readonly underruns: UnderrunReport | null;
  /** Seek to a fraction of the track, 0–1. The waveform works in fractions; the deck works in
   * frames; this is the one place that conversion lives. */
  seekToFraction: (fraction: number) => void;

  /** Bind to the element that should show progress. Written imperatively, never through React. */
  readonly progressRef: React.RefObject<HTMLDivElement | null>;

  load: (file: File) => void;
  onDragOver: (event: React.DragEvent<HTMLElement>) => void;
  onDragLeave: (event: React.DragEvent<HTMLElement>) => void;
  onDrop: (event: React.DragEvent<HTMLElement>) => void;
}

export function useTrackLoader(deckId: DeckId, deps: TrackLoaderDeps): TrackLoaderApi {
  const { openGraph, openCache } = deps;
  const deckRef = useRef<Deck | null>(null);
  const analysisHostRef = useRef<AnalysisHost | null>(null);
  const progressRef = useRef<HTMLDivElement | null>(null);
  /** The buffer the progress loop is watching. A ref, not state, because the rAF loop below must
   * see the current value without being torn down and rebuilt on every phase change. */
  const watchedRef = useRef<{ mipmaps: SharedArrayBuffer; frameCount: number } | null>(
    null,
  );
  /** Guards against a second drop landing mid-load and two pipelines racing to the store. */
  const loadTokenRef = useRef(0);

  const [phase, setPhase] = useState<LoadPhase>('empty');
  const [error, setError] = useState<string | null>(null);
  const [dropActive, setDropActive] = useState(false);
  const [elapsedMillis, setElapsedMillis] = useState<number | null>(null);
  const [analysisMillis, setAnalysisMillis] = useState<number | null>(null);
  const [trackTitle, setTrackTitle] = useState<string | null>(null);
  const [durationSeconds, setDurationSeconds] = useState<number | null>(null);
  const [analysis, setAnalysis] = useState<TrackAnalysis | null>(null);
  const [analysisSource, setAnalysisSource] = useState<AnalysisSource | null>(null);
  const [cacheMissReason, setCacheMissReason] = useState<string | null>(null);
  const [syncNote, setSyncNote] = useState<string | null>(null);
  const [hotCues, setHotCues] = useState<readonly number[]>(() =>
    new Array<number>(HOT_CUE_COUNT).fill(Number.NaN),
  );
  const [loopActive, setLoopActive] = useState(false);

  /** The loaded track's content hash, so cue changes know which record to update (P3.10). */
  const hashRef = useRef<string | null>(null);
  /**
   * FR-13's manual grid correction, or null when the detector's answer stands (P3.12).
   *
   * State rather than a ref because the grid it produces is read by React — the readout, the loop
   * quantization badge and the deck's `setBeatGrid` effect all depend on it, and a ref would leave
   * every one of them showing the detected grid until something else re-rendered.
   */
  const [gridOverride, setGridOverride] = useState<GridOverride | null>(null);
  /** Taps in the current run. A ref, not state: a tap is a gesture, and re-rendering the deck on
   * every one of thirty-two taps would put a hand's timing through React's scheduler. */
  const tapsRef = useRef<readonly Tap[]>([]);
  /** Only the *count* reaches React, because the UI shows it against `TAP_TRUSTWORTHY`. */
  const [tapCount, setTapCount] = useState(0);
  /** The tempo a too-short run would produce. Shown, never applied — see `TrackLoaderApi`. */
  const [tapPreviewBpm, setTapPreviewBpm] = useState<number | null>(null);
  const [gridNote, setGridNote] = useState<string | null>(null);
  /** Reused by the 10 Hz poll — a fresh array ten times a second would be pure garbage. */
  const cueScratchRef = useRef(new Float64Array(HOT_CUE_COUNT));
  const [signals, setSignals] = useState<SharedArrayBuffer | null>(null);
  const [frameCount, setFrameCount] = useState<number | null>(null);
  const [sampleRateHz, setSampleRateHz] = useState<number | null>(null);
  const [transportPhase, setTransportPhase] = useState<TransportPhase>('paused');
  const [normalise, setNormalise] = useState(true);
  const [pitchFader, setPitchFaderState] = useState(0.5);
  const [pitchRange, setPitchRangeState] = useState<number>(DEFAULT_PITCH_RANGE);
  const [pitchBend, setPitchBendState] = useState<PitchBend>(0);

  /**
   * This deck's keys, and the hints printed under it — one list, read from the source's own table
   * (P2.8's rule) and narrowed to this deck (P3.7's).
   *
   * `useMemo` because it is both a dependency of the keyboard effect and a value handed out on every
   * render: a fresh array each time would tear the source down and rebuild it on every state change.
   */
  const bindings = useMemo(() => bindingsForDeck(deckId), [deckId]);
  const keyBindingHints = useMemo(
    () => bindings.map((binding) => ({ code: binding.code, label: binding.label })),
    [bindings],
  );

  /** This deck's control ids. The tables are deck-indexed since P3.7; nothing below names a deck. */
  const pitchControl = PITCH_CONTROL[deckId];
  const transportControl = TRANSPORT_CONTROL[deckId];

  const loadTrackIntoSession = useSessionStore((state) => state.loadTrack);
  const setPlayingInSession = useSessionStore((state) => state.setPlaying);

  /**
   * The control bus (P2.8), owned here because this hook owns the deck the events end up at.
   *
   * Not a module singleton: a global bus is untestable and, more concretely, deck B at M3 wants its
   * own subscriber rather than a shared one filtering by id. `useRef` with a lazy init so StrictMode's
   * double mount does not build two.
   */
  const busRef = useRef<ControlBus | null>(null);
  busRef.current ??= new ControlBus();
  const bus = busRef.current;

  /**
   * What the pitch fader currently means, in a ref rather than in state.
   *
   * The subscriber below is registered once and must see the *current* range and bend without being
   * torn down and rebuilt on every change — and a subscriber that rebuilt on every input event would
   * unsubscribe and resubscribe hundreds of times during one drag. The same reasoning `watchedRef`
   * uses for the progress loop.
   */
  const pitchRef = useRef<{ range: number; bend: PitchBend }>({
    range: DEFAULT_PITCH_RANGE,
    bend: 0,
  });
  pitchRef.current = { range: pitchRange, bend: pitchBend };
  /** The fader position, for the callbacks that re-send it without wanting to depend on it. */
  const pitchFaderRef = useRef(0.5);
  pitchFaderRef.current = pitchFader;

  /**
   * The one subscriber, and **since P3.6 it terminates both halves of Architecture §3's split.**
   *
   * Subscribed to the `deck.a` *namespace* rather than to a list of ids, which is what P2.8 wrote it
   * for and what has now paid: `deck.a.pitch` started arriving here with no change to that line.
   * Each event is offered to both tables and at most one claims it — `toTransportEvent` returns null
   * for the fader, `toEngineCommand` returns null for Play — and neither table knows the other
   * exists. That is Architecture §2's structural/continuous split as two lookups rather than as two
   * code paths.
   *
   * **The subscriber owns the pitch state, and the UI only publishes.** That is a deliberate
   * inversion of the obvious arrangement, and it is what makes the keyboard's nudge keys and the
   * fader's nudge buttons literally the same path rather than two paths that agree today: a `KeyD`
   * keydown and a `pointerdown` on `+` both arrive as `deck.a.nudge.up` at value 1, and everything
   * downstream of this line has no way to tell them apart. A MIDI jog wheel at FF-1 joins by
   * emitting the same event. The alternative — the UI setting its own state and *also* publishing —
   * would leave the keyboard path needing its own copy of the logic, which is how two paths start
   * agreeing and stop.
   */
  useEffect(() => {
    /** Applies a bend and re-sends the fader, because the bend is context rather than a command. */
    const applyBend = (bend: PitchBend): void => {
      setPitchBendState(bend);
      pitchRef.current = { ...pitchRef.current, bend };
      sendPitch(pitchFaderRef.current);
    };

    const sendPitch = (normalized: number): void => {
      const command = toEngineCommand(pitchControl.FADER, normalized, {
        rangeFraction: pitchRef.current.range,
        bend: pitchRef.current.bend,
      });
      if (command !== null) {
        // `sendCommand` stamps the AudioContext clock as it writes, which is the near end of
        // Architecture §4.5's latency probe. Every one of these is a probe sample from a real
        // session rather than from `/bench` — which is what makes NFR-P1 measurable in the field,
        // and it was not before this prompt.
        deckRef.current?.sendCommand(command);
      }
    };

    return bus.subscribe(`deck.${deckId}`, (event) => {
      const transportEvent = toTransportEvent(event.id, event.value);
      if (transportEvent !== null) {
        deckRef.current?.send(transportEvent);
        return;
      }

      if (event.id === pitchControl.NUDGE_UP) {
        applyBend(event.value > 0 ? 1 : 0);
        return;
      }
      if (event.id === pitchControl.NUDGE_DOWN) {
        applyBend(event.value > 0 ? -1 : 0);
        return;
      }

      if (event.id === pitchControl.FADER) {
        setPitchFaderState(event.value);
        pitchFaderRef.current = event.value;
        sendPitch(event.value);
      }
    });
  }, [bus, deckId, pitchControl]);

  /**
   * The keyboard source, attached to the document for the life of the deck.
   *
   * **One source per deck, each carrying only its own deck's bindings** (P3.7). Two sources on one
   * `document` is not a mistake being tolerated: each has its own held-key set and its own bus, so
   * deck A's `Q` and deck B's `P` are tracked independently and a blur releases both. Sharing one
   * source would mean sharing one bus, and the hook's own note explains why each deck wants its own
   * subscriber rather than a shared one filtering by id.
   */
  useEffect(() => {
    const source = new KeyboardSource({
      bus,
      bindings,
      now: () => deckRef.current?.audioTime ?? 0,
    });
    source.attach(document);
    return () => {
      source.detach();
    };
  }, [bus, bindings]);

  /** Publishes a pointer gesture. The `source` field is the only thing that differs from a key. */
  const publishPointer = useCallback(
    (id: ControlId, value: number): void => {
      bus.publish(controlEvent(id, value, deckRef.current?.audioTime ?? 0, 'pointer'));
    },
    [bus],
  );

  /**
   * Mirrors the audio thread's phase into React, at 10 Hz and **only when it changes**.
   *
   * A rAF poll with `setState` would be sixty reconciles a second to re-render a button whose label
   * changes when a human presses something. A 100 ms poll is imperceptible for a label and costs
   * ten atomic loads a second; the equality check means a steady phase costs no renders at all.
   *
   * The readouts do *not* come through here — they read the buffer directly from their own rAF loop
   * (see `Transport.tsx`), which is the rule Stack §2 actually states.
   */
  useEffect(() => {
    const id = setInterval(() => {
      const deck = deckRef.current;
      const current = deck?.phase ?? 'paused';
      setTransportPhase((previous) => (previous === current ? previous : current));

      if (deck === null) {
        return;
      }
      // FR-03/FR-04's state, polled from the audio thread for the same reason the phase is: it is
      // the side that owns it. `set` means "here", a loop wraps between samples, and a mirror on
      // this side would be a guess about both.
      const cues = deck.readHotCues(cueScratchRef.current);
      setHotCues((previous) => {
        // Identity is the re-render trigger, so only replace when something actually moved.
        // `Object.is` rather than `===` because an empty slot is NaN, and NaN !== NaN.
        for (let slot = 0; slot < HOT_CUE_COUNT; slot += 1) {
          if (!Object.is(previous[slot], cues[slot])) {
            return [...cues];
          }
        }
        return previous;
      });
      const loop = deck.readLoop();
      setLoopActive((previous) => (previous === loop.active ? previous : loop.active));
    }, 100);
    return () => {
      clearInterval(id);
    };
  }, []);

  // The store holds *intent* (session.ts is explicit that `playing` is not an observation), so it
  // follows the phase rather than being written next to every gesture.
  useEffect(() => {
    setPlayingInSession(deckId, transportPhase === 'playing');
  }, [deckId, transportPhase, setPlayingInSession]);

  /**
   * FR-10, applied (P3.2). The gain the loudness stage measured, or unity when it has not settled.
   *
   * Derived on every render rather than stored in state, because it is a function of two things
   * that already are (the record and the toggle) and a third copy of a value is a third thing that
   * can be stale. The stage's own `pending`/`skipped`/`failed` outcomes all fall through to unity,
   * which is the honest answer for each of them: nothing has been measured, so nothing is corrected.
   */
  /**
   * FR-07's grid (P3.9), derived rather than stored — the same reasoning `appliedGain` uses. A
   * second copy of a value that is already in `analysis` is a second thing that can go stale, and a
   * stale grid is a sync that aligns to a beat that is not there.
   */
  const tempoStage = analysis?.stages.tempo;
  const detectedGrid: SyncGrid | null =
    tempoStage?.status === 'done' && tempoStage.value.grid !== null
      ? {
          anchorSeconds: tempoStage.value.grid.anchorSeconds,
          beatSeconds: tempoStage.value.grid.beatSeconds,
        }
      : null;

  /**
   * The grid everything downstream actually uses (P3.12) — the human's correction if there is one.
   *
   * **Substituted here, at the single point the detected grid was already derived**, which is what
   * makes FR-13 reach Sync, the audio thread, the loop quantizer and the readout without any of them
   * learning that manual corrections exist. The `setBeatGrid` effect below, `sync`, and
   * `loopQuantization` all read this and were not otherwise touched by this prompt.
   */
  const beatGrid: SyncGrid | null = effectiveGrid(detectedGrid, gridOverride);

  /**
   * Both grids, for the gesture handlers.
   *
   * The same shape as `quantizationRef` below and for the same reason: those callbacks are memoised
   * on `persistOverride` alone, so that pressing a pad does not rebuild them sixty times a second —
   * and a grid captured in their closure would be the one from the render that created them. A nudge
   * must act on the grid that is on screen now.
   *
   * `detectedGrid` is carried separately because `fitTaps` needs the *detected* downbeat as its
   * fallback when a paused tap has no track-time anchor to offer.
   */
  const effectiveGridRef = useRef(beatGrid);
  effectiveGridRef.current = beatGrid;
  const detectedGridRef = useRef(detectedGrid);
  detectedGridRef.current = detectedGrid;

  // The audio thread needs its own copy — it is the side that aligns, and it is also where the
  // *other* deck reads this deck's grid from. Sent on change only; `Deck` re-arms it across a node
  // rebuild.
  useEffect(() => {
    deckRef.current?.setBeatGrid(
      beatGrid === null
        ? null
        : { anchorSeconds: beatGrid.anchorSeconds, beatSeconds: beatGrid.beatSeconds },
    );
  }, [beatGrid?.anchorSeconds, beatGrid?.beatSeconds]);

  /**
   * FR-03's "persisted" (P3.10). Written whenever the set changes, into the cache's **cue** store.
   *
   * That store is deliberately not the analysis one: `ANALYSIS_SCHEMA_VERSION` throws a whole
   * generation of records away when a detector improves, which is right for a computed BPM and would
   * be data loss for a cue point a human placed by ear.
   *
   * Fire-and-forget, like the analysis write: a store that failed must not interrupt a performance,
   * and the worst outcome is cues that are gone next session.
   */
  useEffect(() => {
    const hash = hashRef.current;
    if (hash === null || sampleRateHz === null) {
      return;
    }
    const record: CachedHotCues = {
      hash: hash as CachedHotCues['hash'],
      frames: [...hotCues],
      sampleRateHz,
      updatedAt: Date.now(),
    };
    void openCache().then((cache) => cache?.writeHotCues(record));
  }, [hotCues, sampleRateHz, openCache]);

  /**
   * FR-13's corrections, persisted (P3.12) — "a fix the user has to redo is not a fix".
   *
   * ⚠️ **Called from the gestures, not driven by an effect on the value**, and the difference is a
   * bug rather than a preference. An effect watching `gridOverride` cannot tell a *correction* from
   * a *restore*: loading a track resets the override to null, which would fire the effect against
   * the newly-loaded track's hash and delete the correction that was about to be read back for it.
   * Every write here is therefore reached from something the user did.
   *
   * Clearing **deletes the row** rather than storing an absence — the absence of a row already means
   * "no correction", and two ways to say one thing is how the next reader ends up handling one of
   * them. Fire-and-forget like the cue write: a failed store must not interrupt a performance.
   *
   * No sample rate travels with it. A grid is seconds on both fields, so unlike a cue there is
   * nothing to rescale when the device rate changes — see `grid-edit.ts`.
   */
  const persistOverride = useCallback(
    (next: GridOverride | null): void => {
      const hash = hashRef.current;
      if (hash === null) {
        return;
      }
      setGridOverride(next);
      void openCache().then((cache) =>
        next === null
          ? cache?.clearGridOverride(hash as CachedGridOverride['hash'])
          : cache?.writeGridOverride({
              ...next,
              hash: hash as CachedGridOverride['hash'],
            }),
      );
    },
    [openCache],
  );

  const loudness = analysis?.stages.loudness;
  const measuredGain =
    loudness?.status === 'done' ? loudness.value.gain : DECK_DEFAULT_TRACK_GAIN;
  const appliedGain = normalise ? measuredGain : DECK_DEFAULT_TRACK_GAIN;

  // Sent on change only. The deck ramps it (15 ms) rather than stepping, so toggling the bypass
  // mid-track is a level change and not a click — which matters because toggling it mid-track is
  // precisely how a human checks that any of this works.
  useEffect(() => {
    deckRef.current?.setTrackGain(appliedGain);
  }, [appliedGain]);

  /**
   * The rate the deck is running at, derived rather than stored.
   *
   * A fourth copy of this number — after the fader position, the range and the bend — is a fourth
   * thing that can be stale. It is recomputed from the same function the mapping table uses, so a
   * readout can never disagree with what was written to the ring.
   */
  const pitchRate =
    normalizedToRate(pitchFader, pitchRange) * (1 + pitchBend * PITCH_NUDGE);

  const setPitchFader = useCallback(
    (normalized: number): void => {
      publishPointer(pitchControl.FADER, normalized);
    },
    [publishPointer, pitchControl],
  );

  /**
   * Changing the range re-sends the fader, because the same position now means a different rate.
   *
   * Easy to forget and silent when forgotten: the fader would sit showing +4% while the deck kept
   * playing at the value the old range implied, until somebody happened to touch it again.
   */
  const setPitchRange = useCallback(
    (rangeFraction: number): void => {
      setPitchRangeState(rangeFraction);
      pitchRef.current = { ...pitchRef.current, range: rangeFraction };
      publishPointer(pitchControl.FADER, pitchFaderRef.current);
    },
    [publishPointer, pitchControl],
  );

  /** Press and release of a nudge, published rather than applied. The subscriber owns what happens
   * next, which is what makes this identical to holding `A` or `D`. */
  const nudge = useCallback(
    (direction: -1 | 1, held: boolean): void => {
      publishPointer(
        direction < 0 ? pitchControl.NUDGE_DOWN : pitchControl.NUDGE_UP,
        held ? 1 : 0,
      );
    },
    [publishPointer, pitchControl],
  );

  /**
   * FR-07, in the order P3.9 fixes: tempo, then phase.
   *
   * The tempo half goes through the **pitch fader**, not around it. That is P3.9's "a rate change
   * through P3.6's existing path" read literally, and it is what keeps the UI honest: the fader
   * moves, the percentage readout changes, and the rate reaches the ring through the same mapping
   * table a finger would use. A sync that set the rate behind the fader's back would leave the
   * control lying about the deck it controls.
   *
   * **The range may have to widen**, and that is a real behaviour rather than a workaround. FR-05's
   * fader is ±8% by default, so it cannot reach a 128 → 174 BPM match at all; a DJ switching to a
   * wider range is exactly what the hardware asks of them. The smallest range that reaches the
   * target is chosen, because range is resolution and a wider one than necessary makes every later
   * manual nudge coarser.
   */
  const sync = useCallback(
    (partner: SyncPartner): void => {
      if (beatGrid === null) {
        setSyncNote('this deck has no beatgrid to sync from');
        return;
      }
      if (partner.beatGrid === null) {
        setSyncNote('the other deck has no beatgrid to sync to');
        return;
      }

      // Folded to the octave: 174 against 87 is one beat in two, and asking for rate 0.5 would be
      // treating a musical relationship as a tempo error (see `dsp/sync.ts`).
      const target = bestTempoMatchRate(
        beatGrid,
        partner.beatGrid,
        partner.pitchRate,
        DECK_MAX_RATE,
      );
      if (!Number.isFinite(target)) {
        setSyncNote('no playable rate matches that tempo');
        return;
      }

      const range = PITCH_RANGES.find((candidate) => Math.abs(target - 1) <= candidate);
      if (range === undefined) {
        setSyncNote(
          `needs ${((target - 1) * 100).toFixed(1)}%, beyond the widest fader range`,
        );
        return;
      }

      // The bend is deliberately left alone: it is a held button, and a hand on it during a sync is
      // a hand that means it.
      if (range !== pitchRange) {
        setPitchRange(range);
      }
      setPitchFader(rateToNormalized(target, range));
      // Phase second. The processor waits for the rate ramp above to settle before it acts — see
      // `#applyPendingSync` — so this being sent immediately is correct rather than racy.
      deckRef.current?.syncPhase();
      setSyncNote(null);
    },
    [beatGrid, pitchRange, setPitchFader, setPitchRange],
  );

  const hotCue = useCallback((action: 'set' | 'clear' | 'jump', slot: number): void => {
    deckRef.current?.hotCue(action, slot);
  }, []);

  /**
   * FR-04's loop machine. The **only** decision made on this side is whether to snap.
   *
   * That one belongs here because it needs the tempo's *confidence*, which is analysis the audio
   * thread has no reason to carry — and P3.10's rule is explicit that a low-confidence grid must
   * still loop, unquantized and honestly so.
   */
  // A corrected grid is trusted outright (P3.12): a human tapped along to the track, and leaving the
  // detector's low confidence in place would fix the grid and leave loops still refusing to snap to
  // it — the one outcome FR-13's escape hatch must not have.
  const tempoConfidence = effectiveConfidence(
    tempoStage?.status === 'done' ? tempoStage.value.confidence : 0,
    gridOverride,
  );
  const quantization = loopQuantization(beatGrid, tempoConfidence);
  const quantizationRef = useRef(quantization);
  quantizationRef.current = quantization;

  const loop = useCallback(
    (action: 'auto' | 'in' | 'out' | 'exit' | 'reloop', beats?: number): void => {
      deckRef.current?.loop(action, beats, quantizationRef.current);
    },
    [],
  );

  const readLoop = useCallback((): {
    readonly startSeconds: number;
    readonly endSeconds: number;
  } | null => {
    const deck = deckRef.current;
    if (deck === null) {
      return null;
    }
    const region = deck.readLoop();
    if (!region.active || deck.sampleRateHz <= 0) {
      return null;
    }
    return {
      startSeconds: region.startFrames / deck.sampleRateHz,
      endSeconds: region.endFrames / deck.sampleRateHz,
    };
  }, []);

  // -------------------------------------------------------------------------
  // FR-13's manual grid tools (P3.12)
  // -------------------------------------------------------------------------

  /**
   * One tap.
   *
   * ⚠️ **Records both clocks**, because a user taps along with what they *hear* and what they hear is
   * the track at the deck's current rate. At +8% on the pitch fader the audible beat is 8% faster
   * than the track's own tempo, so a period taken from the wall clock would be stored 8% wrong — and
   * plausible enough that nobody would question the readout. `fitTaps` picks the track clock whenever
   * the playhead actually advanced across the run; see `grid-edit.ts`.
   */
  const tapGrid = useCallback((): void => {
    const now = performance.now() / 1000;
    tapsRef.current = currentTapRun([
      ...tapsRef.current,
      { wallSeconds: now, trackSeconds: deckRef.current?.positionSeconds ?? 0 },
    ]);
    setTapCount(tapsRef.current.length);

    const fit = fitTaps(tapsRef.current, detectedGridRef.current);
    if (fit.grid === null) {
      setTapPreviewBpm(null);
      // Not an error while a run is still building — the count in the UI is the feedback, and a note
      // on every one of the first three taps would be noise.
      setGridNote(
        fit.failure === 'too-few-taps'
          ? null
          : fit.failure === 'out-of-range'
            ? `That is outside ${String(MANUAL_BPM_MIN)}–${String(MANUAL_BPM_MAX)} BPM. Keep tapping, or start again.`
            : 'Those taps were not a tempo. Start again.',
      );
      return;
    }

    // ⚠️ **The fit is not the decision.** `fitTaps` answers "is this a tempo"; `tapStage` answers
    // "is it good enough to act on", and conflating the two is what shipped a four-tap grid into the
    // audio thread under a counter still asking for thirty-two. Below the bar the number is shown
    // and goes no further — no override, so nothing downstream can see it, and in particular
    // `effectiveConfidence` never gets the chance to call it certain.
    if (tapStage(tapsRef.current.length) !== 'applied') {
      setTapPreviewBpm(bpmOf(fit.grid.beatSeconds));
      setGridNote(null);
      return;
    }

    setTapPreviewBpm(null);
    persistOverride(overrideFrom(fit.grid, 'tap', Date.now()));
    setGridNote(
      fit.clock === 'wall'
        ? 'Tempo set from your taps. Play the track and tap again to place the downbeat too.'
        : null,
    );
  }, [persistOverride]);

  /** Throw away the current run without touching the stored grid. */
  const resetTaps = useCallback((): void => {
    tapsRef.current = [];
    setTapCount(0);
    setTapPreviewBpm(null);
    setGridNote(null);
  }, []);

  /** Move the grid earlier or later without changing its tempo. Positive is later. */
  const nudgeGridBy = useCallback(
    (millis: number): void => {
      const grid = effectiveGridRef.current;
      if (grid === null) {
        setGridNote('No grid to nudge — tap a tempo first.');
        return;
      }
      const next = nudgeGrid(grid, millis);
      if (next === null) {
        return;
      }
      persistOverride(overrideFrom(next, 'nudge', Date.now()));
      setGridNote(null);
    },
    [persistOverride],
  );

  /** FR-13's halve/double — the fix for the octave error P3.3 spends most of its length on. */
  const scaleGrid = useCallback(
    (factor: 0.5 | 2): void => {
      const grid = effectiveGridRef.current;
      if (grid === null) {
        setGridNote('No grid to change — tap a tempo first.');
        return;
      }
      const next = scaleGridTempo(grid, factor);
      if (next === null) {
        setGridNote(
          `That would go outside ${String(MANUAL_BPM_MIN)}–${String(MANUAL_BPM_MAX)} BPM, so it was not applied.`,
        );
        return;
      }
      persistOverride(overrideFrom(next, 'octave', Date.now()));
      setGridNote(null);
    },
    [persistOverride],
  );

  /** Back to whatever the detector said. The row is deleted, not overwritten with a copy of it. */
  const clearGridOverride = useCallback((): void => {
    tapsRef.current = [];
    setTapCount(0);
    setTapPreviewBpm(null);
    setGridNote(null);
    persistOverride(null);
  }, [persistOverride]);

  const readSyncPhaseError = useCallback((): number => {
    return deckRef.current?.readSyncPhaseError() ?? Number.NaN;
  }, []);

  const pressPlay = useCallback((): void => {
    publishPointer(transportControl.PLAY, 1);
  }, [publishPointer, transportControl]);

  const pressCue = useCallback((): void => {
    publishPointer(transportControl.CUE, 1);
  }, [publishPointer, transportControl]);

  const releaseCue = useCallback((): void => {
    publishPointer(transportControl.CUE, 0);
  }, [publishPointer, transportControl]);

  const readPositionSeconds = useCallback((): number => {
    const deck = deckRef.current;
    return deck === null ? 0 : deck.positionSeconds;
  }, []);

  const readDeckPeak = useCallback((): number | null => {
    return deckRef.current?.readDeckPeak() ?? null;
  }, []);

  /** P2.10's telemetry source, pointed at the deck by P2.12. Consuming, so each call is a window
   * rather than a running total — see `Deck.takeLatencyWindow`. */
  /** Architecture §12's watchdog poll. A no-op before the first track, like every deck call here. */
  const sampleClockDrift = useCallback((): void => {
    deckRef.current?.sampleClockDrift();
  }, []);

  const takeLatencyWindow = useCallback(
    (minimumCount: number): DistributionMs | null =>
      deckRef.current?.takeLatencyWindow(minimumCount) ?? null,
    [],
  );

  const readCueSeconds = useCallback((): number => {
    const deck = deckRef.current;
    return deck === null ? 0 : deck.cuePointFrames / deck.sampleRateHz;
  }, []);

  const seekToFraction = useCallback((fraction: number): void => {
    const deck = deckRef.current;
    if (deck === null) {
      return;
    }
    const frames = deck.trackSpec?.frameCount ?? 0;
    deck.seek(Math.min(frames - 1, Math.max(0, Math.round(fraction * frames))));
  }, []);

  // The progress line (UX §5.3: "a thin progress line runs along the overview strip").
  //
  // rAF reading a SharedArrayBuffer and writing `style.width` by hand — Stack §2's rule that no
  // React state sits in a rAF or SAB-reading path, and the same shape as the master meter (P0.7)
  // and the FPS readout (P0.8). A 6-minute track ticks ~260 times during analysis; through
  // setState that would be 260 reconciles to move one bar.
  useEffect(() => {
    let frame = 0;

    const tick = (): void => {
      frame = requestAnimationFrame(tick);

      const watched = watchedRef.current;
      const bar = progressRef.current;
      if (watched === null || bar === null || watched.frameCount === 0) {
        return;
      }

      const fraction = readFramesDone(watched.mipmaps) / watched.frameCount;
      bar.style.width = `${(Math.min(1, fraction) * 100).toFixed(1)}%`;
    };

    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
    };
  }, []);

  const load = useCallback(
    (file: File): void => {
      const token = ++loadTokenRef.current;
      // `performance.now()` at the very top: FR-11's promise is measured from the user's action,
      // not from the moment our async work happens to begin.
      const startedAt = performance.now();

      setError(null);
      setElapsedMillis(null);
      setAnalysisMillis(null);
      setAnalysis(null);
      setAnalysisSource(null);
      setCacheMissReason(null);
      setSyncNote(null);
      setLoopActive(false);
      // Cleared here and refilled from the cache below. A new track must not inherit the previous
      // one's cue points — they are positions in a different piece of audio.
      hashRef.current = null;
      setHotCues(new Array<number>(HOT_CUE_COUNT).fill(Number.NaN));
      // Likewise for FR-13's correction: a grid belongs to one piece of audio, and inheriting the
      // previous track's would be a wrong grid presented as a deliberate fix. Safe to clear rather
      // than persist because `hashRef` is null on this line — `persistOverride` is the only thing
      // that writes, it is never reached from here, and it refuses without a hash anyway.
      setGridOverride(null);
      tapsRef.current = [];
      setTapCount(0);
      setTapPreviewBpm(null);
      setGridNote(null);
      watchedRef.current = null;
      if (progressRef.current !== null) {
        progressRef.current.style.width = '0%';
      }

      // Refused before any decode, so the message can name the format instead of relaying
      // `EncodingError`. See formats.ts for why the browser is not the oracle here.
      const verdict = verifyFormat(file.name);
      if (!verdict.accepted) {
        setPhase('failed');
        setError(verdict.reason);
        return;
      }

      setTrackTitle(file.name);
      setPhase('decoding');

      void (async (): Promise<void> => {
        try {
          // The graph is opened on demand and shared: `openGraph` is memoised by `App`, so the
          // second deck to be loaded joins the first one's context rather than opening another.
          deckRef.current ??= (await openGraph()).createDeck(deckId);
          const deck = deckRef.current;

          // ── FR-12, part one: read once, fingerprint, then decode ────────────────────────
          //
          // The order is forced. `decodeAudioData` **detaches** the buffer it is given, so the hash
          // and the length have to be taken first — and reading the file twice to avoid caring
          // would cost more than the hash does (8.5 ms over 7.68 MiB, measured; see
          // `content-hash.ts`).
          const bytes = await file.arrayBuffer();
          const fileBytes = bytes.byteLength;
          const hash = await hashContent(bytes);

          const info = await deck.loadEncoded(bytes);
          if (token !== loadTokenRef.current) {
            return;
          }

          const trackMemory = deck.trackMemory;
          const trackSpec = deck.trackSpec;
          if (trackMemory === null || trackSpec === null) {
            throw new Error('The deck decoded the file but produced no track memory.');
          }

          setPhase('analysing');
          setDurationSeconds(info.durationSeconds);

          // ── FR-12, part two: ask the cache ──────────────────────────────────────────────────
          //
          // **Only now**, because two of the four things that identify a record are properties of
          // *this machine's decode* rather than of the file: `decodeAudioData` resamples to the
          // device rate, so the same bytes become a different frame count at 44.1 and 48 kHz, and a
          // mipmap built at one rate indexes wrongly at the other. `usabilityOf` refuses that, and
          // it is not hypothetical — this project's own stakeholder had a device report 44.1 kHz one
          // day and 48 kHz the next.
          const identity: DecodedTrackIdentity = {
            hash,
            fileBytes,
            frameCount: info.frameCount,
            sampleRateHz: info.sampleRateHz,
          };
          const layout = computeWaveformMipmapLayout({
            frameCount: trackSpec.frameCount,
            sampleRateHz: info.sampleRateHz,
          });

          hashRef.current = hash;
          const cache = await openCache();

          // FR-03's cues come back before anything else does, so a track that was already known is
          // playable *with its cues* the moment the transport appears. Converted rather than
          // refused when the device rate has changed since they were set: a cue is a time, and a
          // time rescales exactly — unlike a mipmap, which is why `usabilityOf` refuses those.
          const storedCues = await cache?.readHotCues(identity.hash);
          const restored = cuesFor(storedCues ?? null, info.sampleRateHz, HOT_CUE_COUNT);
          deck.restoreHotCues(restored);
          setHotCues(restored);

          // FR-13's correction comes back with the cues, and for the same reason: it is user data
          // about *this* track, keyed by the same content hash, so a renamed copy finds it too.
          // Restored **before** the analysis lands, so a corrected grid is never briefly replaced by
          // the detected one on screen. No rate conversion — a grid is seconds (`grid-edit.ts`).
          setGridOverride(await (cache?.readGridOverride(identity.hash) ?? null));
          const lookup = (await cache?.read(identity)) ?? { miss: 'no-record' as const };
          if (token !== loadTokenRef.current) {
            return;
          }

          // The blob's length was checked against the record; this checks it against the layout
          // *this* build would compute. They differ when the mipmap ladder changes without the
          // schema version being bumped — a mistake `ANALYSIS_SCHEMA_VERSION` documents and this
          // catches anyway, because a waveform drawn from a mismatched ladder is silent nonsense.
          const hit =
            lookup.hit?.mipmaps.byteLength === layout.totalBytes ? lookup.hit : undefined;

          if (hit !== undefined) {
            watchedRef.current = null;
            if (progressRef.current !== null) {
              progressRef.current.style.width = '100%';
            }
            setAnalysis({
              mipmaps: hit.mipmaps,
              layout,
              // Zero, and it is the honest number: this is what the *mipmap stage* cost on this
              // load, and it did not run. What it cost when it did run is in the record.
              elapsedMillis: 0,
              stages: hit.record.stages,
            });
            setAnalysisSource('cache');
            setCacheMissReason(null);
            setAnalysisMillis(0);
            setElapsedMillis(performance.now() - startedAt);
            setSignals(deck.signalBuffer);
            setFrameCount(info.frameCount);
            setSampleRateHz(info.sampleRateHz);
            setPhase('ready');
            loadTrackIntoSession(deckId, {
              title: file.name,
              frameCount: info.frameCount,
              sampleRateHz: info.sampleRateHz,
              durationSeconds: info.durationSeconds,
            });
            return;
          }

          setAnalysisSource('worker');
          setCacheMissReason(CACHE_MISS_REASON[lookup.miss ?? 'no-record']);

          analysisHostRef.current ??= new AnalysisHost();
          const started = analysisHostRef.current.analyse(
            {
              trackMemory,
              track: trackSpec,
              sampleRateHz: info.sampleRateHz,
            },
            {
              // FR-11's progressive availability, wired through (P3.1). Every stage that settles
              // republishes the record, so the waveform draws and the deck becomes playable the
              // moment mipmaps land — while tempo and key are still running.
              //
              // Guarded by the load token for the same reason the await below is: a stale run's
              // stages must not overwrite the record of the track that replaced it.
              onStage: (partial) => {
                if (token !== loadTokenRef.current) {
                  return;
                }
                setAnalysis(partial);
                if (partial.stages.mipmaps.status === 'done') {
                  setAnalysisMillis(partial.elapsedMillis);
                  // **FR-11's number, stopped where FR-11 stops** — at the waveform, not at the end
                  // of the pipeline (corrected at P3.3).
                  //
                  // It was taken after `await started.done` until P3.3, which was very nearly right
                  // while the pipeline was mipmaps plus three stages that declined: the two numbers
                  // differed by a millisecond. Onsets and tempo made them differ by seconds, and the
                  // readout labelled "waveform ready in" started reporting a figure that had nothing
                  // to do with the waveform — a budget measuring the wrong thing, arrived at by the
                  // thing it measures growing rather than by anyone changing it.
                  setElapsedMillis(performance.now() - startedAt);
                  // Ready means "the user can use this", and that is true once there is a waveform
                  // and a playable deck. Waiting for key detection to finish before enabling the
                  // transport would be holding the instrument hostage to a readout.
                  setPhase('ready');
                }
              },
            },
          );

          // Watched from here, so the progress line moves during the analysis rather than after it.
          watchedRef.current = {
            mipmaps: started.mipmaps,
            frameCount: trackSpec.frameCount,
          };

          const result = await started.done;
          if (token !== loadTokenRef.current) {
            return;
          }

          watchedRef.current = null;
          if (progressRef.current !== null) {
            progressRef.current.style.width = '100%';
          }

          setAnalysis(result);
          setAnalysisMillis(result.elapsedMillis);

          // ── FR-12, part three: remember it ──────────────────────────────────────────────────
          //
          // After the record is published, never before: storing is an optimisation for next time
          // and must not delay this time. `write` never throws — a full quota, a private window or
          // a browser without OPFS all return false, and the only consequence is that this track is
          // analysed again next time.
          const record: CachedAnalysis = {
            hash,
            schemaVersion: ANALYSIS_SCHEMA_VERSION,
            fileBytes,
            frameCount: info.frameCount,
            sampleRateHz: info.sampleRateHz,
            layout: result.layout,
            mipmapBytes: result.mipmaps.byteLength,
            stages: result.stages,
            analysisMillis: result.elapsedMillis,
            storedAt: Date.now(),
          };
          void cache?.write(record, result.mipmaps);
          // `elapsedMillis` is deliberately *not* re-stamped here. It was set when the mipmaps
          // landed and that is the moment FR-11 budgets; overwriting it with the pipeline's total
          // is the bug the comment above describes.
          setSignals(deck.signalBuffer);
          setFrameCount(info.frameCount);
          setSampleRateHz(info.sampleRateHz);
          setPhase('ready');

          loadTrackIntoSession(deckId, {
            // The filename is the title until M9's library gives tracks real metadata
            // (session.ts says so). It stays on this machine: FR-44 forbids it reaching telemetry,
            // and nothing here sends anything anywhere.
            title: file.name,
            frameCount: info.frameCount,
            sampleRateHz: info.sampleRateHz,
            durationSeconds: info.durationSeconds,
          });
        } catch (cause: unknown) {
          if (token !== loadTokenRef.current) {
            return;
          }

          // FR-42: explain, never present a broken deck. The real error goes to the console for
          // whoever is debugging; the user gets UX §8's sentence.
          console.error('[synapse] track load failed', cause);
          watchedRef.current = null;
          setPhase('failed');
          setError(
            cause instanceof Error && cause.message.includes('cross-origin isolated')
              ? cause.message
              : decodeFailureMessage(),
          );
        }
      })();
    },
    [deckId, loadTrackIntoSession, openGraph, openCache],
  );

  const onDragOver = useCallback((event: React.DragEvent<HTMLElement>): void => {
    // Selected text, a dragged link, an image from another tab: none of those are a track, and
    // lighting the deck up for them promises something the drop will not deliver. `types` is all
    // the browser exposes mid-drag — it hides the items on purpose so a page cannot inventory what
    // is dragged over it — so this can promise "a file" and never "an MP3".
    if (!dragCarriesFile(event.dataTransfer.types)) {
      return;
    }

    // Both of these are required. Without `preventDefault` on dragover the browser refuses the drop
    // entirely and navigates to the file instead — the single most common way a drop target
    // silently does nothing.
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setDropActive(true);
  }, []);

  const onDragLeave = useCallback((event: React.DragEvent<HTMLElement>): void => {
    // `dragleave` fires when the pointer crosses into a *child* element too, which would flicker
    // the outline off and on across every control in the deck. `relatedTarget` is where the pointer
    // went; if that is still inside us, the drag never actually left.
    const movedTo = event.relatedTarget;
    if (movedTo instanceof Node && event.currentTarget.contains(movedTo)) {
      return;
    }
    setDropActive(false);
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLElement>): void => {
      event.preventDefault();
      setDropActive(false);

      const file = event.dataTransfer.files.item(0);
      if (file === null) {
        // A drag of selected text, a URL, or a folder on some platforms. Not an error worth a
        // message — the user will see nothing happened and try again with a file.
        return;
      }
      load(file);
    },
    [load],
  );

  useEffect(() => {
    return () => {
      analysisHostRef.current?.dispose();
      void deckRef.current?.dispose();
    };
  }, []);

  return {
    deckId,
    phase,
    error,
    dropActive,
    elapsedMillis,
    analysisMillis,
    trackTitle,
    durationSeconds,
    analysis,
    analysisSource,
    cacheMissReason,
    signals,
    frameCount,
    sampleRateHz,
    transportPhase,
    playing: isRunning(transportPhase),
    pressPlay,
    pressCue,
    releaseCue,
    keyBindings: keyBindingHints,
    normalise,
    setNormalise,
    appliedGain,
    pitchFader,
    setPitchFader,
    pitchRange,
    setPitchRange,
    pitchBend,
    nudge,
    pitchRate,
    beatGrid,
    gridOverride,
    detectedGrid,
    tapCount,
    tapPreviewBpm,
    gridNote,
    tapGrid,
    resetTaps,
    nudgeGridBy,
    scaleGrid,
    clearGridOverride,
    sync,
    syncNote,
    hotCues,
    hotCue,
    loopActive,
    loopQuantization: quantization,
    loop,
    readLoop,
    readSyncPhaseError,
    readPositionSeconds,
    readCueSeconds,
    readDeckPeak,
    takeLatencyWindow,
    sampleClockDrift,
    // A getter rather than a memoised value: the deck does not exist until the first load, and a
    // value captured at mount would be null for the life of the hook.
    get latencyInfo(): DeckLatencyInfo | null {
      return deckRef.current?.info ?? null;
    },
    get underruns(): UnderrunReport | null {
      return deckRef.current?.underruns ?? null;
    },
    seekToFraction,
    progressRef,
    load,
    onDragOver,
    onDragLeave,
    onDrop,
  };
}
